// components/MasteryCell.tsx
//
// V28-C (blueprint C8). Named + numbered mastery map TILE coloured by readiness
// band — the cockpit-grid counterpart to MasteryRing. Maps to the prototype
// `.cell` (kit.css 748-802 / index.html 741-803). MasteryRing.tsx stays in use
// elsewhere; this is a NEW addition, not a replacement.
//
// Visual contract (blueprint C8 + prototype `.cell`):
//   - A 4px LEFT band-rail (the prototype's `::before`) in the band colour.
//   - A band-dot top-right so the band is readable BEYOND colour (WCAG 1.4.1).
//   - cell-num (zero-padded order) + cell-name (Italian argomento name).
//   - 5 band modifiers via `masteryBand()` / `masteryBandColors()` — the single
//     source of truth in tokens (forte / buono / da-rinforzare / debole /
//     da-iniziare). `da-iniziare` is the empty state: dashed border + hollow
//     dot, white fill, muted rail.
//   - Soft tinted fill derived from the band's solid colour (low-alpha overlay,
//     the same idiom tokens use for `*Soft`) so we stay on the token palette
//     without hardcoding the prototype's bespoke pastel hexes.
//
// Light-only: colours resolve through `masteryBandColors(band, tokens.mode)`
// and `tokens.mode` is hard-pinned to 'light' app-wide (state/theme.tsx F1).
// Purely presentational — props drive every cell; no fetch, no state.

import type { CSSProperties, JSX } from 'react';
import { useTokens } from '../state/theme';
import { useReducedMotion } from '../state/use-reduced-motion';
import {
  masteryBand,
  masteryBandColors,
  MASTERY_BANDS,
  type ThemeMode,
} from '../tokens';
import { formatArgomentoNumber } from '../state/canonical-learning';

/**
 * Convert a `#rrggbb` hex to an `rgba(r,g,b,alpha)` string. Used to derive the
 * soft tinted cell fill from the band's solid colour, matching the tokens
 * `*Soft` idiom (e.g. `rightSoft: 'rgba(46,141,111,0.12)'`). Falls back to the
 * input untouched if it isn't a 6-digit hex (so non-hex band colours pass
 * through harmlessly).
 */
function softTint(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return hex;
  const r = parseInt(m[1], 16);
  const g = parseInt(m[2], 16);
  const b = parseInt(m[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export interface MasteryCellProps {
  /** Stable argomento id — drives the data-testid + click target. */
  argomentoId: string;
  /** Display order, rendered zero-padded (e.g. 1 → "01"). */
  order: number;
  /** Italian argomento name (the cell label). */
  nameIt: string;
  /** Average mastery, 0..1. */
  averageMastery: number;
  /** Total principles in the argomento (0 → never started → da-iniziare). */
  principlesTotal: number;
  /** Observed principles (0 with total>0 → started-but-no-evidence → da-iniziare). */
  principlesObserved?: number;
  /** Optional click handler — when set the cell renders as a <button>. */
  onSelect?: (argomentoId: string) => void;
}

export function MasteryCell({
  argomentoId,
  order,
  nameIt,
  averageMastery,
  principlesTotal,
  principlesObserved,
  onSelect,
}: MasteryCellProps): JSX.Element {
  const tokens = useTokens();
  const reducedMotion = useReducedMotion();
  const mode: ThemeMode = tokens.mode;

  const band = masteryBand({ averageMastery, principlesTotal, principlesObserved });
  const isEmpty = band.id === MASTERY_BANDS.daIniziare.id;
  const { color: bandColor } = masteryBandColors(band, mode);

  // Empty ("da-iniziare"): white fill + dashed border + hollow muted dot + muted
  // rail. Otherwise: soft tinted fill + solid rail/dot in the band colour.
  const railColor = isEmpty ? tokens.textFaint : bandColor;
  const fill = isEmpty ? tokens.surface : softTint(bandColor, 0.12);

  const numLabel = formatArgomentoNumber(argomentoId, order);
  const action = onSelect ? " Apri l'argomento." : '';

  // DP4 (2026-07-13): show HOW MUCH evidence sits behind the band so a
  // "Debole" born from one simulation reads as thin early evidence, not as a
  // verdict on lessons the student never did.
  const evidence = !isEmpty && typeof principlesObserved === 'number' && principlesTotal > 0
    ? `${principlesObserved} su ${principlesTotal} regole messe alla prova`
    : null;

  // Aria-label per WCAG 1.4.1 — colour-independent description of the band.
  const ariaLabel = isEmpty
    ? `${numLabel} ${nameIt}: nessuna evidenza dalle risposte.${action}`
    : `${numLabel} ${nameIt}: livello ${band.label}${evidence ? ` — ${evidence}` : ''}.${action}`;

  // Shared inner content (rail + dot + num + name). The rail is an absolutely
  // positioned 4px bar (the prototype `::before`); the dot sits top-right.
  const inner = (
    <>
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          background: railColor,
        }}
      />
      <span
        aria-hidden="true"
        data-band={band.id}
        style={{
          position: 'absolute',
          top: 10,
          right: 10,
          width: 9,
          height: 9,
          borderRadius: '50%',
          background: isEmpty ? 'transparent' : bandColor,
          border: isEmpty
            ? `1.5px solid ${tokens.textFaint}`
            : '1px solid rgba(255,255,255,0.6)',
        }}
      />
      <span
        style={{
          fontFamily: tokens.fonts.body,
          fontSize: 11,
          fontWeight: 700,
          color: tokens.textMuted,
        }}
      >
        {numLabel}
      </span>
      <span
        style={{
          fontFamily: tokens.fonts.body,
          fontSize: 13,
          fontWeight: 700,
          lineHeight: 1.2,
          color: tokens.text,
        }}
      >
        {nameIt}
      </span>
      {evidence && (
        <span
          data-testid="mastery-cell-evidence"
          style={{
            fontFamily: tokens.fonts.body,
            fontSize: 10.5,
            fontWeight: 500,
            color: tokens.textMuted,
          }}
        >
          {evidence}
        </span>
      )}
    </>
  );

  const baseStyle: CSSProperties = {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: 8,
    minHeight: 64,
    padding: '10px 12px',
    borderRadius: tokens.radius.md,
    border: isEmpty
      ? `1px dashed ${tokens.lineStrong}`
      : `1px solid ${tokens.line}`,
    background: fill,
    overflow: 'hidden',
    textAlign: 'left',
    // Honour reduced-motion: drop the hover-lift transition entirely.
    transition: reducedMotion ? 'none' : 'transform 0.14s ease, box-shadow 0.16s ease',
  };

  if (onSelect) {
    return (
      <button
        type="button"
        data-testid={`mastery-cell-${argomentoId}`}
        data-band={band.id}
        aria-label={ariaLabel}
        onClick={() => onSelect(argomentoId)}
        style={{
          ...baseStyle,
          cursor: 'pointer',
          width: '100%',
          color: tokens.text,
        }}
      >
        {inner}
      </button>
    );
  }

  return (
    <div
      data-testid={`mastery-cell-${argomentoId}`}
      data-band={band.id}
      aria-label={ariaLabel}
      role="img"
      style={baseStyle}
    >
      {inner}
    </div>
  );
}
